import { Grid, useMediaQuery, useTheme } from '@material-ui/core';
import React, { useCallback, useEffect, useState } from 'react';
import { Field, reduxForm, change, getFormValues } from 'redux-form';
import TextFields from '../../../control/textfield/TextFields';
import * as FocusActions from '../../../actions/FocusAction';
import { useDispatch, useSelector } from 'react-redux';
import * as ApiEvent from '../../../api/ApiEvent';

const formId = 'RegistrationForm';

const emailRegex =
    /^[a-zA-Z0-9.!#$%&'+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(\.[a-zA-Z0-9-]+)*\.[a-zA-Z]{2,}$/;

/**
 * (code >= 0x0E01 && code <= 0x0E2E) || // ก - ฮ (main Thai letters)
 * (code >= 0x0E30 && code <= 0x0E3A) || // vowels like ะ า ำ ิ ี ึ ื ุ ู
 * (code >= 0x0E40 && code <= 0x0E4E);   // tone marks, diacritics, etc.
 */
const alphabeticRegex =
    /^[\u0E01-\u0E2E\u0E30-\u0E39\u0E40-\u0E4E\u0E47\u0E48\u0E49\u0E4A\u0E4C\u0E4D\u0E4E a-zA-Z'-]+$/;

const validateForm = (values) => {
    const errorMsg = 'กรุณาระบุข้อมูล';
    const errors = {};
    if (!values.email) {
        errors.email = errorMsg;
    }
    if (!emailRegex.test(values.email)) {
        errors.email = 'กรุณากรอกอีเมลให้ตรงตามรูปแบบ';
    }

    if (!values.firstName) {
        errors.firstName = errorMsg;
    } else if (!alphabeticRegex.test(values.firstName)) {
        errors.firstName =
            'ชื่อสามารถกรอกได้เฉพาะอักษรไทย/อังกฤษ และเว้นวรรคเท่านั้น';
    }
    if (!values.lastName) {
        errors.lastName = errorMsg;
    } else if (!alphabeticRegex.test(values.lastName)) {
        errors.lastName =
            'นามสกุลสามารถกรอกได้เฉพาะอักษรไทย/อังกฤษ และเว้นวรรคเท่านั้น';
    }
    if (!values.companyName) {
        errors.companyName = errorMsg;
    }
    if (!values.position) {
        errors.position = errorMsg;
    }
    return errors;
};

const defaultValues = {
    email: null,
    firstName: null,
    lastName: null,
    companyName: null,
    position: null,
};

const RegisterForm = ({ handleSubmit, initialize, onSubmit }) => {
    const theme = useTheme();
    const isSmUp = useMediaQuery(theme.breakpoints.up('sm'));
    const dispatch = useDispatch();
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        initialize({ ...defaultValues });
    }, []);

    const fetchEmail = useCallback(async (email) => {
        try {
            setIsLoading(true);
            // fetch lookup external
            const res = await ApiEvent.getExternalUserEventByEmail({
                email,
            });
            if (res.data) {
                return res.data;
            }
        } catch (error) {
        } finally {
            setIsLoading(false);
        }
    }, []);

    const handleBlurEmail = useCallback(
        async (event) => {
            try {
                if (event.target.value) {
                    if (emailRegex.test(event.target.value)) {
                        const res = await fetchEmail(event.target.value);
                        const { firstName, lastName, companyName, position } =
                            res;
                        if (firstName && lastName && companyName && position) {
                            dispatch(change(formId, 'firstName', firstName));
                            dispatch(change(formId, 'lastName', lastName));
                            dispatch(
                                change(formId, 'companyName', companyName)
                            );
                            dispatch(change(formId, 'position', position));
                        }
                    }
                }
            } catch (error) {}
        },
        [fetchEmail, dispatch]
    );

    return (
        <form onSubmit={handleSubmit(onSubmit)}>
            <Grid container spacing={isSmUp ? 2 : 0}>
                <Grid item xs={12}>
                    <Field
                        name="email"
                        label="อีเมล"
                        component={TextFields}
                        placeholder="กรุณาระบุ"
                        loading={isLoading}
                        onBlur={handleBlurEmail}
                        disabled={isLoading}
                        required
                        maxLength={50}
                    />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <Field
                        name="firstName"
                        label="ชื่อ"
                        placeholder="ชื่อ"
                        component={TextFields}
                        disabled={isLoading}
                        maxLength={30}
                        required
                    />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <Field
                        name="lastName"
                        label="นามสกุล"
                        placeholder="นามสกุล"
                        component={TextFields}
                        disabled={isLoading}
                        maxLength={30}
                        required
                    />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <Field
                        name="companyName"
                        label="ชื่อหน่วยงาน / ชื่อบริษัท"
                        placeholder="ชื่อหน่วยงาน / ชื่อบริษัท"
                        component={TextFields}
                        disabled={isLoading}
                        maxLength={50}
                        required
                    />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <Field
                        name="position"
                        label="ตำแหน่ง"
                        placeholder="ตำแหน่ง"
                        component={TextFields}
                        disabled={isLoading}
                        maxLength={30}
                        required
                    />
                </Grid>
            </Grid>
        </form>
    );
};

export default reduxForm({
    form: formId,
    validate: validateForm,
    onSubmitFail: (errors, dispatch, submitError, props) =>
        dispatch(FocusActions.setFocusName(errors, props)),
})(RegisterForm);
